import type { components } from '@/generated/ac.js';
import { authService } from '@/services/auth.js';

export type Invitation = components['schemas']['Invitation'];
export type CreateInvitationReq = components['schemas']['CreateInvitationReq'];
export type User = components['schemas']['User'];
export type UserReq = components['schemas']['UserReq'];
export type AuthorizeRequest = components['schemas']['AuthorizeRequest'];
export type AuthorizeResponse = components['schemas']['AuthorizeResponse'];

class AcClient {
    private async _authHeaders(): Promise<Record<string, string>> {
        await authService.ensureValidToken();
        return { Authorization: `Bearer ${authService.getToken()!}` };
    }

    async getInvitations(pageToken: string | null = null): Promise<{ invitations: Invitation[]; nextPageToken: string | null }> {
        const headers: Record<string, string> = await this._authHeaders();
        if (pageToken) headers['X-Page-Token'] = pageToken;

        const res = await fetch('/api/ac/v1/invitation', { headers });
        if (!res.ok) throw new Error(`Failed to load invitations: ${res.status}`);

        const invitations: Invitation[] = await res.json();
        return { invitations, nextPageToken: res.headers.get('X-Next-Page-Token') };
    }

    async createInvitation(email: string, type: string): Promise<Invitation> {
        const body: CreateInvitationReq = { email, type };
        const res = await fetch('/api/ac/v1/invitation', {
            method: 'POST',
            headers: { ...await this._authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error(`Failed to create invitation: ${res.status}`);
        return res.json();
    }

    async getInvitation(invitationId: string): Promise<Invitation> {
        const res = await fetch(`/api/ac/v1/invitation/${invitationId}`, {
            headers: await this._authHeaders()
        });
        if (!res.ok) throw new Error(`Failed to load invitation: ${res.status}`);
        return res.json();
    }

    async deleteInvitation(invitationId: string): Promise<void> {
        const res = await fetch(`/api/ac/v1/invitation/${invitationId}`, {
            method: 'DELETE',
            headers: await this._authHeaders()
        });
        if (!res.ok) throw new Error(`Failed to delete invitation: ${res.status}`);
    }

    async getUserById(userId: string): Promise<User> {
        const res = await fetch(`/api/ac/v1/user/${userId}`, {
            headers: await this._authHeaders()
        });
        if (!res.ok) throw new Error(`Failed to load user: ${res.status}`);
        return res.json();
    }

    async createUser(req: UserReq): Promise<User> {
        const res = await fetch('/api/ac/v1/user', {
            method: 'POST',
            headers: { ...await this._authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(req)
        });
        if (!res.ok) throw new Error(`Failed to create user: ${res.status}`);
        return res.json();
    }

    async updateUser(userId: string, req: UserReq): Promise<User> {
        const res = await fetch(`/api/ac/v1/user/${userId}`, {
            method: 'PUT',
            headers: { ...await this._authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify(req)
        });
        if (!res.ok) throw new Error(`Failed to update user: ${res.status}`);
        return res.json();
    }

    async authenticate(payload: Record<string, string>): Promise<AuthorizeResponse> {
        const res = await fetch('/api/ac/v1/token/authenticate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Auth failed');
        return res.json();
    }

    async refreshToken(): Promise<AuthorizeResponse | null> {
        const res = await fetch('/api/ac/v1/token/refresh');
        if (res.status === 401) return null;
        if (!res.ok) throw new Error('Token refresh failed');
        return res.json();
    }

    async logout(): Promise<void> {
        const res = await fetch('/api/ac/v1/token/logout');
        if (!res.ok) throw new Error('logout failed');
    }

    async getUsers(pageToken: string | null = null): Promise<{ users: User[]; nextPageToken: string | null }> {
        const headers: Record<string, string> = await this._authHeaders();
        if (pageToken) headers['X-Page-Token'] = pageToken;

        const res = await fetch('/api/ac/v1/user', { headers });
        if (!res.ok) throw new Error(`Failed to load users: ${res.status}`);

        const users: User[] = await res.json();
        return { users, nextPageToken: res.headers.get('X-Next-Page-Token') };
    }

}

export const acClient = new AcClient();